// App.js

import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import "./App.css";
import Menu from "./components/Menu/Menu";
//import ChronologyPanel from "./components/Chronology/ChronologyPanel";
import ImageGrid from "./components/ImageGrid/ImageGrid";
import ImageGeo from "./components/ImageGeo/ImageGeo";
import IntroScreenLight from "./components/IntroScreen/IntroScreenLight";
import PresentationModal from "./components/PresentationModal/PresentationModal";
import StreetViewModal from "./components/StreetView/StreetViewModal";
import ImageCellFullModal from "./components/ImageFullModal/ImageCellFullModal";
import { MenuProvider } from "./components/Menu/context/MenuProvider";
import StreetViewAlertModal from "./components/Common/StreetViewAlertModal";
import { useFilters } from "./hooks/useFilters";
import { useSort } from "./hooks/useSort";
import { useColorMapping } from "./hooks/useColorMapping";
import { useImagesData } from "./hooks/useImagesData"; // enrichissement des datas
import { useContentData } from "./hooks/useContentData";
import { useGoogleStreetViewController } from "./hooks/useGoogleStreetViewController";
import { useActiveFilterColor } from "./hooks/useActiveFilterColor";
export default App;

//import { useGridMetrics } from "./hooks/useGridMetrics";

function App() {
  const [introDone, setIntroDone] = useState(false);
  const [showPresentation, setShowPresentation] = useState(false);

  // ✅ Images ENRICHIES dès le chargement
  const { images, loading, periods } = useImagesData();
  const content = useContentData();

  // États UI Globaux
  const [searchAdresse, setSearchAdresse] = useState("");
  const [searchMaison, setSearchMaison] = useState("");
  const [viewMode, setViewMode] = useState("grid");

  //const [selectedPeriod, setSelectedPeriod] = useState(null);

  // Hook pour les filtres
  const {
    filters,
    activeFilters,
    toggleFilter,
    resetFilters,
    hierarchy,
    filteredImages: hookFilteredImages,
    deactivateChildren,
  } = useFilters(images, "typoloA");

  // Liste de base après filtres
  const baseFiltered = useMemo(() => {
    return hookFilteredImages || images;
  }, [hookFilteredImages, images]);

  // Hook pour le tri et la recherche
  const {
    sortKey,
    setSortKey,
    sortNomActive,
    sortNomAsc,
    sortAdresseActive,
    sortAdresseAsc,
    toggleSortNom,
    toggleSortAdresse,
    finalSortedImages,
    shuffle,
    resetSort,
    sortDateActive,
    sortDateAsc,
    toggleSortDate,
    selectedPeriod,
    //setSelectedPeriod,
    setPeriod,
  } = useSort(baseFiltered, searchAdresse, searchMaison);

  /* Grille et infinitescroll */
  // Ref pour contrôler Virtuoso (la grille) depuis App
  const virtuosoRef = useRef(null);

  // Reset du scroll quand les filtres changent
  useEffect(() => {
    if (viewMode === "grid" && virtuosoRef.current) {
      // Virtuoso a sa propre méthode scrollTo
      virtuosoRef.current.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [searchAdresse, searchMaison, activeFilters.length, viewMode, filters, selectedPeriod]); 
  // enlever "filters" si on ne veut pas que le scroll se remette à zéro quand on change de typologie
  // ajouter dates


  //const { minItems } = useGridMetrics(200, 4);
  /*const finalImages = useMemo(() => {
    if (!selectedPeriod) return finalSortedImages;

    return finalSortedImages.filter((img) => {
      const d = img.sortDate;
      if (!d) return false;

      if (d.type === "point") {
        return d.year >= selectedPeriod.start && d.year <= selectedPeriod.end;
      }

      return d.start >= selectedPeriod.start && d.end <= selectedPeriod.end;
    });
  }, [selectedPeriod, finalSortedImages]);*/

  const finalImages = useMemo(() => {
    if (loading) return [];
    return finalSortedImages;
  }, [finalSortedImages, loading]);

  // Handlers mémoïsés
  const handleSearchAdresse = useCallback((value) => {
    setSearchAdresse(value);
  }, []);

  const handleSearchMaison = useCallback((value) => {
    setSearchMaison(value);
  }, []);

  const handleRestore = useCallback(() => {
    setSearchAdresse("");
    setSearchMaison("");
    resetFilters();
    //setVisibleCount(50);
    // 🔤 facultatif : reset tris
    // setSortKey("");
    // 🎲 mélange des images
    resetSort(); // 👈 remet tri à zéro
    shuffle(); // 🎲 mélange des images
  }, [resetFilters, resetSort, shuffle]);

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
    //setVisibleCount(50);
  }, []);
  /**/
  // ✅ ColorMap toujours nécessaire pour le Menu et ImageGeo
  // Mais plus besoin de le passer à ImageGrid
  const colorMap = useColorMapping(Object.keys(hierarchy || {}));

  // Couleur du filtre actif
  const colorFiltre = useActiveFilterColor(activeFilters, colorMap);

  // Street View controller
  const streetViewController = useGoogleStreetViewController();

  // modal image full
  const [fullImage, setFullImage] = useState(null);

  const openFullImage = useCallback((img) => {
    setFullImage(img);
  }, []);

  const closeFullImage = useCallback(() => {
    setFullImage(null);
  }, []);

  // ✅ OPTIMISATION : useMemo pour éviter le re-render du MenuProvider
  const menuValue = useMemo(
    () => ({
      filters,
      activeFilters,
      toggleFilter,
      resetFilters,
      hierarchy,
      deactivateChildren,
      viewMode,
      setViewMode: handleViewModeChange,
      colorMap,
      colorFiltre,
      content,
      onSearchAdresse: handleSearchAdresse,
      onSearchMaison: handleSearchMaison,
      searchAdresse,
      searchMaison,
      sortKey,
      setSortKey,
      sortNomAsc,
      sortNomActive,
      sortAdresseAsc,
      sortAdresseActive,
      toggleSortNom,
      toggleSortAdresse,
      onRestore: handleRestore,
      onOpenPresentation: () => setShowPresentation(true),
      sortDateActive,
      sortDateAsc,
      toggleSortDate,
      resetSort,
      shuffle,
      selectedPeriod,
      setPeriod,
      //setSelectedPeriod,
      periods,
    }),
    [
      filters,
      activeFilters,
      toggleFilter,
      resetFilters,
      hierarchy,
      deactivateChildren,
      viewMode,
      handleViewModeChange,
      colorMap,
      colorFiltre,
      content,
      handleSearchAdresse,
      handleSearchMaison,
      searchAdresse,
      searchMaison,
      sortKey,
      setSortKey,
      sortNomAsc,
      sortNomActive,
      sortAdresseAsc,
      sortAdresseActive,
      toggleSortNom,
      toggleSortAdresse,
      handleRestore,
      sortDateActive,
      sortDateAsc,
      toggleSortDate,
      resetSort,
      shuffle,
      selectedPeriod,
      setPeriod,
      //setSelectedPeriod,
      periods,
    ],
  );

  //const [svAlertShown, setSvAlertShown] = useState(false);
  const [pendingSVRequest, setPendingSVRequest] = useState(null);

  const svAlertShownRef = useRef(false);


  /*const handleStreetViewRequest = useCallback(
    (cellData) => {
      // première fois : afficher alerte
      if (!svAlertShown) {
        setPendingSVRequest(cellData);
        setSvAlertShown(true);
        return;
      }

      // fois suivantes : comportement actuel
      streetViewController.openStreetView(cellData);
    },
    [svAlertShown, streetViewController],
  );*/
  // App.js

const handleStreetViewRequest = useCallback(
  (cellData) => {
    // On vérifie la valeur actuelle de la ref
    if (!svAlertShownRef.current) {
      setPendingSVRequest(cellData);
      
      // On met à jour la ref (ne déclenche pas de re-render)
      svAlertShownRef.current = true; 
      return;
    }

    streetViewController.openStreetView(cellData);
  },
  [streetViewController] // ✅ svAlertShown a disparu des dépendances
);

  return (
    <>
      {!introDone ? (
        <IntroScreenLight
          onEnter={() => setIntroDone(true)}
          onOpenPresentation={() => setShowPresentation(true)}
        />
      ) : (
        <div
          className={`app-layout ${sortDateActive ? "with-chronology" : ""}`}
        >
          <MenuProvider value={menuValue}>
            {/* <Menu total={images.length} visible={finalSortedImages.length} />*/}
            <Menu total={images.length} visible={finalImages.length} />
          </MenuProvider>

          {viewMode === "grid" ? (
            <ImageGrid
              images={finalImages} //{imagesWithPadding} //{finalSortedImages}
              //loading={loading}
              onOpenStreetView={handleStreetViewRequest}
              //onOpenStreetView={streetViewController.openStreetView}
              onOpenFullImage={openFullImage}
              gridRef={virtuosoRef}
            />
          ) : (
            <ImageGeo
              images={finalImages} //{finalSortedImages}
              onOpenFullImage={openFullImage}
            />
          )}

          {/*sortDateActive && (
            <ChronologyPanel
              selectedPeriod={selectedPeriod}
              onSelectPeriod={setSelectedPeriod}
            />
          )*/}

          <StreetViewModal
            {...streetViewController.streetViewState}
            onClose={streetViewController.closeStreetView}
          />
          {fullImage && (
            <ImageCellFullModal
              img={fullImage}
              onClose={closeFullImage}
              onOpenStreetView={handleStreetViewRequest}
            />
          )}

          <StreetViewAlertModal
            visible={!!pendingSVRequest}
            message={content?.msg?.streetViewAlert}
            onConfirm={() => {
              streetViewController.openStreetView(pendingSVRequest);
              setPendingSVRequest(null);
            }}
            onCancel={() => {
              setPendingSVRequest(null);
            }}
          />

          {/*process.env.NODE_ENV === "development" && (
            <div className="msgdebug">
              <small>
                Street View : {getUsagePercentage()}% utilisé ce mois
              </small>
            </div>
          )*/}

          {/*process.env.NODE_ENV !== "production" && (
            <div className="msgdebug">
              StreetView mode : {process.env.REACT_APP_STREETVIEW_MODE}
            </div>
          )*/}
        </div>
      )}

      {showPresentation && (
        <PresentationModal
          visible={showPresentation}
          onClose={() => setShowPresentation(false)}
          content={content}
          cible={"presentation" /*!introDone ? "presentation" : "demo"*/}
        />
      )}
    </>
  );
}

/*
const menuValue = {
  // === Gestion des filtres ===
  filters,              // État des filtres actifs (objet {nomFiltre: boolean})
  activeFilters,        // Tableau des clés des filtres actuellement actifs
  toggleFilter,         // Fonction pour activer/désactiver un filtre
  resetFilters,         // Fonction pour réinitialiser tous les filtres
  hierarchy,            // Structure hiérarchique des filtres (parents/enfants)
  deactivateChildren,   // Fonction pour désactiver tous les enfants d'un filtre parent
  
  // === Gestion de l'affichage ===
  viewMode,             // Mode d'affichage actuel ("grid" ou "geo")
  setViewMode: handleViewModeChange, // Fonction pour basculer entre grille et carte
  
  // === Mapping des couleurs ===
  colorMap,             // Association typologie → couleur (objet {typologie: couleur})
  colorFiltre,          // Couleur du filtre actuellement actif (pour le logo)
  
  // === Contenu textuel ===
  content,              // Contenu JSON (présentation, définitions des typologies)
  
  // === Recherche ===
  onSearchAdresse: handleSearchAdresse,  // Callback pour la recherche par adresse
  onSearchMaison: handleSearchMaison,    // Callback pour la recherche par domonyme
  searchAdresse,        // Valeur actuelle du champ de recherche adresse
  searchMaison,         // Valeur actuelle du champ de recherche domonyme
  
  // === Tri ===
  sortKey,              // Clé de tri générique (utilisée pour tris personnalisés)
  setSortKey,           // Fonction pour définir la clé de tri
  sortNomAsc,           // Ordre de tri par nom (true = croissant, false = décroissant)
  sortNomActive,        // Indique si le tri par nom est actif
  sortAdresseAsc,       // Ordre de tri par adresse (true = croissant, false = décroissant)
  sortAdresseActive,    // Indique si le tri par adresse est actif
  toggleSortNom,        // Fonction pour activer/inverser le tri par nom
  toggleSortAdresse,    // Fonction pour activer/inverser le tri par adresse
  
  // === Actions globales ===
  onRestore: handleRestore,  // Fonction pour réinitialiser recherches, filtres et tri
  
  // === Navigation ===
  onOpenPresentation: () => setShowPresentation(true), // Ouvre la modale "À propos"
};
 */
